/**
 * The requirement heat maps' arithmetic: a cross-tab of two requirement fields.
 *
 * Issue #94: coverage and TBRs, "the two main diagrams that we have actual data
 * for". Both are the same table - a segment or subsystem down the side, the
 * level across the top - counted three ways per cell: requirements, those a
 * verification case verifies, and those whose status is TBD / TBR / TBC.
 *
 * Counted HERE, from `GET /api/flowdown`, which carries level, segment,
 * subsystem, `is_unresolved` and `verified_by` on every row. The Views card for
 * the TBR map used to say it was blocked because `GET /api/coverage` groups by
 * one axis at a time - true, but beside the point: the flowdown rows are the
 * whole set already, in one request.
 *
 * `verified` is "a verification case names it in `verifies`", which is what
 * `repository.coverage_by` counts: `verified_by` is the other end of that same
 * relationship. One gap between the two: /api/flowdown walks from the roots, so
 * a requirement inside a source cycle that no root leads to is not in it, and
 * would be in /api/coverage. The findings report such a cycle in its own right.
 *
 * Pure and exported, asserted in `heatmap.test.ts`. A cross-tab that counts a
 * requirement twice - flowdown repeats one under each of its sources - draws a
 * hotter cell than the model has, and nothing on screen says it is wrong.
 */

import type { FlowdownRow } from '../../schema'
import { requirementKey } from './layout'

/** A requirement field a heat map can lay out along one side. */
export type HeatAxis = 'level' | 'segment' | 'subsystem'

/** What a cell's colour measures. */
export type HeatMeasure = 'unresolved' | 'coverage'

/** The key of a requirement stating nothing for an axis - as GET /api/coverage names it. */
export const UNSET = '(unset)'

export interface HeatCell {
  total: number
  /** Verified by at least one verification case. */
  verified: number
  /** Status TBD, TBR or TBC. */
  unresolved: number
}

export interface HeatTable {
  /** Keys down the side and across the top, in display order. */
  rows: string[]
  columns: string[]
  /** `cells[row][column]`; a combination nobody states is absent. */
  cells: Record<string, Record<string, HeatCell>>
  rowTotals: Record<string, HeatCell>
  columnTotals: Record<string, HeatCell>
  total: HeatCell
  /** The most unresolved requirements in any one cell, for the TBR scale. */
  maxUnresolved: number
}

const empty = (): HeatCell => ({ total: 0, verified: 0, unresolved: 0 })

/** The key a row falls under on an axis: a level as its number, text as stated. */
export function axisKey(row: FlowdownRow, axis: HeatAxis): string {
  if (axis === 'level') return typeof row.level === 'number' ? String(row.level) : UNSET
  const value = String(row[axis] ?? '')
  return value || UNSET
}

/** How a key is shown: `L2` for a level, the value itself otherwise. */
export function axisLabel(axis: HeatAxis, key: string): string {
  if (key === UNSET) return axis === 'level' ? 'No level' : `No ${axis}`
  return axis === 'level' ? `L${key}` : key
}

/** Levels numerically, text naturally; the unset key last either way. */
export function sortKeys(axis: HeatAxis, keys: Iterable<string>): string[] {
  return [...keys].sort((a, b) => {
    if (a === UNSET || b === UNSET) return Number(a === UNSET) - Number(b === UNSET)
    if (axis === 'level') return Number(a) - Number(b)
    return a.localeCompare(b, undefined, { numeric: true })
  })
}

function add(cell: HeatCell, verified: boolean, unresolved: boolean) {
  cell.total += 1
  if (verified) cell.verified += 1
  if (unresolved) cell.unresolved += 1
}

/**
 * Count every requirement once, by where it falls on two axes.
 *
 * ONCE: flowdown repeats a requirement under each of its sources, so rows are
 * deduplicated by the same key the hierarchy gives its boxes.
 *
 * @param[in] rows - FlowdownRow[] : GET /api/flowdown's items
 * @param[in] rowAxis - HeatAxis : down the side
 * @param[in] columnAxis - HeatAxis : across the top
 * @return HeatTable
 */
export function crossTab(rows: FlowdownRow[], rowAxis: HeatAxis, columnAxis: HeatAxis): HeatTable {
  const cells: Record<string, Record<string, HeatCell>> = {}
  const rowTotals: Record<string, HeatCell> = {}
  const columnTotals: Record<string, HeatCell> = {}
  const total = empty()
  const seen = new Set<string>()

  for (const row of rows) {
    const key = requirementKey(row)
    if (!key || seen.has(key)) continue
    seen.add(key)

    const r = axisKey(row, rowAxis)
    const c = axisKey(row, columnAxis)
    const verified = Array.isArray(row.verified_by) && row.verified_by.length > 0
    const unresolved = row.is_unresolved === true

    cells[r] ??= {}
    cells[r][c] ??= empty()
    rowTotals[r] ??= empty()
    columnTotals[c] ??= empty()
    for (const cell of [cells[r][c], rowTotals[r], columnTotals[c], total]) add(cell, verified, unresolved)
  }

  let maxUnresolved = 0
  for (const byColumn of Object.values(cells)) {
    for (const cell of Object.values(byColumn)) maxUnresolved = Math.max(maxUnresolved, cell.unresolved)
  }

  return {
    rows: sortKeys(rowAxis, Object.keys(rowTotals)),
    columns: sortKeys(columnAxis, Object.keys(columnTotals)),
    cells,
    rowTotals,
    columnTotals,
    total,
    maxUnresolved,
  }
}

/**
 * How hot a cell is, 0 to 1, or null for a cell with no requirements in it.
 *
 * The two maps answer different questions, so they scale differently:
 *
 * - TBRs: the cell's COUNT against the busiest cell. "Where are they
 *   concentrated" is about where the next sweep would clear the most, so ten of
 *   a hundred outranks two of two.
 * - Coverage: the cell's SHARE NOT verified. A coverage gap is relative to what
 *   is there; hot means "most of this is untested", not "this is big".
 *
 * @param[in] cell - HeatCell | undefined : the cell, absent when nobody states it
 * @param[in] measure - HeatMeasure : which map
 * @param[in] table - HeatTable : for the TBR scale's maximum
 * @return number | null
 */
export function intensity(
  cell: HeatCell | undefined,
  measure: HeatMeasure,
  table: Pick<HeatTable, 'maxUnresolved'>,
): number | null {
  if (!cell || cell.total === 0) return null
  if (measure === 'coverage') return (cell.total - cell.verified) / cell.total
  return table.maxUnresolved > 0 ? cell.unresolved / table.maxUnresolved : 0
}
